import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo } from 'react';

type PaginationItem = number | 'ellipsis-left' | 'ellipsis-right';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

const getPaginationItems = (
  currentPage: number,
  totalPages: number
): PaginationItem[] => {
  if (totalPages <= 10) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 6) {
    return [1, 2, 3, 4, 5, 6, 7, 8, 'ellipsis-right', totalPages - 1, totalPages];
  }

  if (currentPage >= totalPages - 5) {
    return [
      1,
      2,
      'ellipsis-left',
      totalPages - 7,
      totalPages - 6,
      totalPages - 5,
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    2,
    'ellipsis-left',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis-right',
    totalPages - 1,
    totalPages,
  ];
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}) => {
  const items = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalPages <= 1) {
    return null;
  }

  const isPreviousDisabled = disabled || currentPage <= 1;
  const isNextDisabled = disabled || currentPage >= totalPages;

  const baseItemClasses =
    'min-w-10 h-10 px-3 inline-flex items-center justify-center border border-light-300 dark:border-graphite-600 text-sm font-medium transition-colors';
  const itemEnabledClasses =
    'bg-white dark:bg-graphite-800 text-primary-600 dark:text-primary-300 hover:bg-light-50 dark:hover:bg-graphite-700';
  const itemDisabledClasses =
    'bg-light-100 dark:bg-graphite-700 text-gray-400 dark:text-gray-500 cursor-not-allowed';
  const itemActiveClasses =
    'bg-primary-600 dark:bg-primary-700 text-white border-primary-600 dark:border-primary-700';

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-center w-full overflow-x-auto"
    >
      <div className="inline-flex rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={isPreviousDisabled}
          className={`${baseItemClasses} ${
            isPreviousDisabled ? itemDisabledClasses : itemEnabledClasses
          }`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {items.map((item, index) => {
          if (item === 'ellipsis-left' || item === 'ellipsis-right') {
            return (
              <span
                key={`${item}-${index}`}
                className={`${baseItemClasses} bg-white dark:bg-graphite-800 text-gray-500 dark:text-gray-400`}
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          const isActive = item === currentPage;
          const isItemDisabled = disabled || isActive;

          return (
            <button
              type="button"
              key={item}
              onClick={() => onPageChange(item)}
              disabled={isItemDisabled}
              aria-current={isActive ? 'page' : undefined}
              className={`${baseItemClasses} ${
                isActive
                  ? itemActiveClasses
                  : isItemDisabled
                    ? itemDisabledClasses
                    : itemEnabledClasses
              }`}
            >
              {item}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={isNextDisabled}
          className={`${baseItemClasses} ${
            isNextDisabled ? itemDisabledClasses : itemEnabledClasses
          }`}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
